<template>
  <div class="audit-log-page">
    <h1>HIPAA Audit Log</h1>
    
    <form @submit.prevent="fetchAuditLogs" class="audit-form">
      <div class="form-row">
        <div class="form-field">
          <label>Account ID</label>
          <input type="text" v-model="filters.account_id" />
        </div>
        <div class="form-field">
          <label>User ID</label>
          <input type="text" v-model="filters.user_id" />
        </div>
        <div class="form-field">
          <label>Patient ID</label>
          <input type="text" v-model="filters.patient_id" />
        </div>
        <div class="form-field">
          <label>First Name</label>
          <input type="text" v-model="filters.first_name" />
        </div>
        <div class="form-field">
          <label>Last Name</label>
          <input type="text" v-model="filters.last_name" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-field">
          <label>Action Type (comma-separated: VIEW, CREATE, UPDATE, DELETE)</label>
          <input type="text" v-model="filters.action_type" placeholder="VIEW, CREATE, UPDATE, DELETE" />
        </div>
        <div class="form-field">
          <label>Resource Type</label>
          <input type="text" v-model="filters.resource_type" />
        </div>
        <div class="form-field">
          <label>Category</label>
          <select v-model="filters.category" @change="onCategoryChange">
            <option value="">All</option>
            <option v-for="category in categories" :key="category" :value="category">{{ category }}</option>
          </select>
        </div>
        <div class="form-field">
          <label>Function</label>
          <select v-model="filters.api_method">
            <option value="">All</option>
            <option v-for="method in filteredMethods" :key="method" :value="method">{{ method }}</option>
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-field">
          <label>Start Date</label>
          <input type="date" v-model="filters.start_date" />
        </div>
        <div class="form-field">
          <label>End Date</label>
          <input type="date" v-model="filters.end_date" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-field">
          <label>Records per Page</label>
          <input type="number" v-model.number="filters.limit" min="1" max="10000" />
        </div>
        <div class="form-field">
          <label>Start from Record #</label>
          <input type="number" v-model.number="filters.offset" min="0" placeholder="0 = first record" />
        </div>
        <div class="form-field">
          <label>Order By</label>
          <select v-model="filters.order_by">
            <option value="timestamp">Timestamp</option>
            <option value="action_type">Action Type</option>
            <option value="resource_type">Resource Type</option>
            <option value="api_method">API Method</option>
          </select>
        </div>
        <div class="form-field">
          <label>Order Direction</label>
          <select v-model="filters.order_dir">
            <option value="DESC">DESC</option>
            <option value="ASC">ASC</option>
          </select>
        </div>
      </div>

      <div class="form-actions">
        <button type="submit" :disabled="loading">Search</button>
        <button type="button" @click="resetForm">Reset</button>
      </div>
    </form>

    <div v-if="loading" class="loading">Loading...</div>

    <div v-if="results && results.length > 0" class="results">
      <div class="results-header">
        <div class="results-summary">
          Total Records: {{ summary.total_records }} | 
          Returned: {{ summary.returned_records }} | 
          Page: {{ summary.page }}
        </div>
        <button @click="exportToCSV" class="export-btn">Export to CSV</button>
      </div>

      <div class="table-container">
        <table class="audit-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Account ID</th>
              <th>User ID</th>
              <th>Account Type</th>
              <th>Username</th>
              <th>Patient ID</th>
              <th>Patient Name</th>
              <th>Action Type</th>
              <th>Resource Type</th>
              <th>Resource ID</th>
              <th>API Method</th>
              <th>IP Address</th>
              <th>Success</th>
              <th>Timestamp</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="record in results" :key="record.id">
              <td>{{ record.id }}</td>
              <td>{{ record.account_id }}</td>
              <td>{{ record.user_id }}</td>
              <td>{{ record.account_type }}</td>
              <td>{{ record.username }}</td>
              <td>{{ record.patient_id }}</td>
              <td>{{ record.patient_name }}</td>
              <td>{{ record.action_type }}</td>
              <td>{{ record.resource_type }}</td>
              <td>{{ record.resource_id }}</td>
              <td>{{ record.api_method }}</td>
              <td>{{ record.ip_address }}</td>
              <td :class="record.success ? 'success' : 'failure'">{{ record.success ? 'Yes' : 'No' }}</td>
              <td>{{ formatDate(record.timestamp) }}</td>
              <td class="details-cell">{{ formatDetails(record.details) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="results && results.length === 0 && !loading" class="no-results">
      No records found.
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import axios from 'axios'
import { CURR_API } from '@/constants/apis'

const loading = ref(false)
const results = ref(null)
const summary = ref({
  total_records: 0,
  returned_records: 0,
  page: 1
})

// Comprehensive list of all API methods from the codebase
const allApiMethods = [
  // Medication
  'GetPatientMeds',
  'GetPatientsMedByID',
  'HoldMedication',
  'ClearCurrentMedicationHold',
  'InsertUpdateMedLogTimes',
  'ChangeOrderForMedLogTimes',
  'MedSignOff',
  'PrnSignOff',
  'InsertAdminPrescriptionInfo',
  'InsertAdminProviderInfo',
  'InsertAdminPharmacyInfo',
  'InsertAdminMecationInfo',
  'InsetPrescription',
  'GetAssignedPatPharmacy',
  'UpdateOrderWithNurseSignature',
  'LookupOrderInfoSigSignOff',
  'GetPrePourMeds',
  
  // Orders
  'GetOrders',
  'GetOrderDetails',
  'GetOrderByAccountOrderID',
  'GetAccountPendingOrders',
  'NEW_GetAccountPendingOrders',
  'GetAccountPendingOrdersByID',
  'NEW_GetAccountPendingOrdersByID',
  'GetALLPatientOrders',
  'GetALLPatientOrdersWithMedications',
  'GetOrderByDates',
  'GetOrdersByCombination',
  'GetOrderNumber',
  
  // CPT/Billing
  'GetPatientCpt',
  'GetPatientCpt2',
  'AddPatientCpt',
  'GetMeterCpt',
  'AddMeterCpt',
  'GetMeasurements',
  'GetMeasurements2',
  'MarkBilled',
  'GetPatientCptBilling',
  'GetPatientCptBilling2',
  'GetBillingInfoByAccountNumber',
  
  // Diagnosis
  // (No specific diagnosis methods found in the codebase)
  
  // Provider
  'GetProvidersForGroup',
  'CreateProviderGroup',
  'GetProviderGroup',
  'DeleteProviderGroup',
  'UpdateProviderOnTeam',
  'GetProviderInfo',
  'GetAllProviders',
  'GetAccountProviders',
  'GetSingleProvider',
  'GetProviderAccountPatients',
  'AssignPatientToProvider',
  'GetProviderAssignedPatient',
  
  // Nurse
  'GetAccountNurse',
  'GetIndividualAccountNurse',
  'GetIndividualAccountAid',
  'GetAccountAids',
  'GetPatientNurse',
  'AssignPatientToNurse',
  'AssigNurseToPatient',
  'DischargeNurseFromPatient',
  'DischargePatientFromCard',
  'LookupNursesInfo',
  'LookupBatchNursesInfo',
  'Add_Nurse',
  'InternalAddNurse',
  'InternalUpdateNurse',
  'DeleteNurse',
  'DeleteNurseInternal',
  'GetAllNursePdfs',
  'UploadNurseFile',
  'AddNewPdf',
  'ListCredentialDocuments',
  'DeleteCredentialDocument',
  'DeleteDirectiveDocument',
  'FileDownload',
  'FileHead',
  'FileBundle',
  'PacmnyBeDocView',
  
  // Account
  'GetAccountInfoByAccountNumber',
  'GetAccountType',
  'GetAccountUsers',
  'GetPendingAccountInfo',
  ' UpdatePendingAccountInfo',
  'GetIndividualPermissions',
  'GetUserAccountPermission',
  'UpdateIndividualPermissions',
  'SendNewSubAccountEmail',
  'SendContactInvitationEmail',
  'UpdateParentAccount',
  'CreateAccount',
  'CreateSubAccount',
  'CreateTwilioSubAccount',
  'AddContact',
  'InsertContact',
  
  // Patient
  'GetCurrentPatientData',
  'GetPatientDevice',
  'AddPatientDevice',
  'RemovePatientDevice',
  'AddPatientInformation',
  'GetNebulizerData',
  'PatientLookupSearch',
  'GetAccountPatients',
  'SavePatientNote',
  'VideoUpload',
  'GetAllNotes',
  'GetNoteByID',
  'GetAllergies',
  
  // Messages
  'FetchMessages',
  'FetchAllMessagesByIDS',
  'FetchMessagesByThreadID',
  'SearchAllMessagesByIDS',
  'SendInternalMessages',
  'SendGroupMessage',
  'DeleteMessageAttachment',
  'MarkMessagesAsRead',
  'SearchContacts',
  'SearchGroups',
  'SearchAllContactsNurseProviders',
  'GetPatientGroups',
  
  // SMS/Auth
  'GetAccountSMSPHone',
  'Sms6DigitText',
  'VerifyLoginCode',
  
  // Insurance
  'SaveNewInsurance',
  
  // Twilio
  'SendConferenceText',
  'SendPhoneCall',
  
  // Other
  'GetAuditLogs',
  'GetAuditLogMethods'
]

// Categorize API methods
const categorizedMethods = {
  'Medication': [
    'GetPatientMeds',
    'GetPatientsMedByID',
    'HoldMedication',
    'ClearCurrentMedicationHold',
    'InsertUpdateMedLogTimes',
    'ChangeOrderForMedLogTimes',
    'MedSignOff',
    'PrnSignOff',
    'InsertAdminPrescriptionInfo',
    'InsertAdminProviderInfo',
    'InsertAdminPharmacyInfo',
    'InsertAdminMecationInfo',
    'InsetPrescription',
    'GetAssignedPatPharmacy',
    'UpdateOrderWithNurseSignature',
    'LookupOrderInfoSigSignOff',
    'GetPrePourMeds'
  ],
  'Orders': [
    'GetOrders',
    'GetOrderDetails',
    'GetOrderByAccountOrderID',
    'GetAccountPendingOrders',
    'NEW_GetAccountPendingOrders',
    'GetAccountPendingOrdersByID',
    'NEW_GetAccountPendingOrdersByID',
    'GetALLPatientOrders',
    'GetALLPatientOrdersWithMedications',
    'GetOrderByDates',
    'GetOrdersByCombination',
    'GetOrderNumber'
  ],
  'CPT/Billing': [
    'GetPatientCpt',
    'GetPatientCpt2',
    'AddPatientCpt',
    'GetMeterCpt',
    'AddMeterCpt',
    'GetMeasurements',
    'GetMeasurements2',
    'MarkBilled',
    'GetPatientCptBilling',
    'GetPatientCptBilling2',
    'GetBillingInfoByAccountNumber'
  ],
  'Provider': [
    'GetProvidersForGroup',
    'CreateProviderGroup',
    'GetProviderGroup',
    'DeleteProviderGroup',
    'UpdateProviderOnTeam',
    'GetProviderInfo',
    'GetAllProviders',
    'GetAccountProviders',
    'GetSingleProvider',
    'GetProviderAccountPatients',
    'AssignPatientToProvider',
    'GetProviderAssignedPatient'
  ],
  'Nurse': [
    'GetAccountNurse',
    'GetIndividualAccountNurse',
    'GetIndividualAccountAid',
    'GetAccountAids',
    'GetPatientNurse',
    'AssignPatientToNurse',
    'AssigNurseToPatient',
    'DischargeNurseFromPatient',
    'DischargePatientFromCard',
    'LookupNursesInfo',
    'LookupBatchNursesInfo',
    'Add_Nurse',
    'InternalAddNurse',
    'InternalUpdateNurse',
    'DeleteNurse',
    'DeleteNurseInternal',
    'GetAllNursePdfs',
    'UploadNurseFile',
    'AddNewPdf',
    'ListCredentialDocuments',
    'DeleteCredentialDocument',
    'DeleteDirectiveDocument',
    'FileDownload',
    'FileHead',
    'FileBundle',
    'PacmnyBeDocView'
  ],
  'Account': [
    'GetAccountInfoByAccountNumber',
    'GetAccountType',
    'GetAccountUsers',
    'GetPendingAccountInfo',
    ' UpdatePendingAccountInfo',
    'GetIndividualPermissions',
    'GetUserAccountPermission',
    'UpdateIndividualPermissions',
    'SendNewSubAccountEmail',
    'SendContactInvitationEmail',
    'UpdateParentAccount',
    'CreateAccount',
    'CreateSubAccount',
    'CreateTwilioSubAccount',
    'AddContact',
    'InsertContact'
  ],
  'Patient': [
    'GetCurrentPatientData',
    'GetPatientDevice',
    'AddPatientDevice',
    'RemovePatientDevice',
    'AddPatientInformation',
    'GetNebulizerData',
    'PatientLookupSearch',
    'GetAccountPatients',
    'SavePatientNote',
    'VideoUpload',
    'GetAllNotes',
    'GetNoteByID',
    'GetAllergies'
  ],
  'Messages': [
    'FetchMessages',
    'FetchAllMessagesByIDS',
    'FetchMessagesByThreadID',
    'SearchAllMessagesByIDS',
    'SendInternalMessages',
    'SendGroupMessage',
    'DeleteMessageAttachment',
    'MarkMessagesAsRead',
    'SearchContacts',
    'SearchGroups',
    'SearchAllContactsNurseProviders',
    'GetPatientGroups'
  ],
  'SMS/Auth': [
    'GetAccountSMSPHone',
    'Sms6DigitText',
    'VerifyLoginCode'
  ],
  'Insurance': [
    'SaveNewInsurance'
  ],
  'Twilio': [
    'SendConferenceText',
    'SendPhoneCall'
  ],
  'Other': [
    'GetAuditLogs',
    'GetAuditLogMethods'
  ]
}

const filters = reactive({
  account_id: '',
  user_id: '',
  patient_id: '',
  first_name: '',
  last_name: '',
  action_type: '',
  resource_type: '',
  start_date: '',
  end_date: '',
  category: '',
  api_method: '',
  limit: 1000,
  offset: 0,
  order_by: 'timestamp',
  order_dir: 'DESC'
})

// Get list of categories
const categories = computed(() => {
  return Object.keys(categorizedMethods).sort()
})

// Filter methods based on selected category
const filteredMethods = computed(() => {
  if (!filters.category || filters.category === '') {
    return allApiMethods.sort()
  }
  return (categorizedMethods[filters.category] || []).sort()
})

// Handle category change - reset function selection if it's not in the new category
const onCategoryChange = () => {
  if (filters.category && filters.api_method) {
    const methodsInCategory = filteredMethods.value
    if (!methodsInCategory.includes(filters.api_method)) {
      filters.api_method = ''
    }
  }
}

const fetchAuditLogs = async () => {
  loading.value = true
  results.value = null

  const payload = {
    AuditLog: {
      API_Meth: 'GetAuditLogs',
      account_id: filters.account_id || null,
      user_id: filters.user_id || null,
      patient_id: filters.patient_id || null,
      first_name: filters.first_name || null,
      last_name: filters.last_name || null,
      action_type: filters.action_type || null,
      resource_type: filters.resource_type || null,
      start_date: filters.start_date || null,
      end_date: filters.end_date || null,
      api_method: filters.api_method || null,
      limit: filters.limit,
      offset: filters.offset,
      order_by: filters.order_by,
      order_dir: filters.order_dir
    }
  }

  try {
    const response = await axios.post(CURR_API + '/tswebhook.php', payload)
    if (response.data && response.data.status === 'success') {
      results.value = response.data.records
      summary.value = {
        total_records: response.data.total_records,
        returned_records: response.data.returned_records,
        page: response.data.page
      }
    } else {
      results.value = []
    }
  } catch (error) {
    console.error('Error fetching audit logs:', error)
    results.value = []
  } finally {
    loading.value = false
  }
}

const resetForm = () => {
  filters.account_id = ''
  filters.user_id = ''
  filters.patient_id = ''
  filters.first_name = ''
  filters.last_name = ''
  filters.action_type = ''
  filters.resource_type = ''
  filters.start_date = ''
  filters.end_date = ''
  filters.category = ''
  filters.api_method = ''
  filters.limit = 1000
  filters.offset = 0
  filters.order_by = 'timestamp'
  filters.order_dir = 'DESC'
  results.value = null
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString()
}

const formatDetails = (details) => {
  if (!details) return ''
  try {
    const parsed = JSON.parse(details)
    return parsed && typeof parsed === 'object' ? (parsed.message || '') : ''
  } catch {
    return ''
  }
}

const exportToCSV = () => {
  if (!results.value || results.value.length === 0) {
    return
  }

  // Define column headers
  const headers = [
    'ID',
    'Account ID',
    'User ID',
    'Account Type',
    'Username',
    'Patient ID',
    'Patient Name',
    'Action Type',
    'Resource Type',
    'Resource ID',
    'API Method',
    'IP Address',
    'User Agent',
    'Session ID',
    'Success',
    'Error Message',
    'Details',
    'Timestamp'
  ]

  // Escape CSV values
  const escapeCSV = (value) => {
    if (value === null || value === undefined) {
      return ''
    }
    const stringValue = String(value)
    // If value contains comma, quote, or newline, wrap in quotes and escape quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return '"' + stringValue.replace(/"/g, '""') + '"'
    }
    return stringValue
  }

  // Create CSV rows
  const csvRows = []
  
  // Add header row
  csvRows.push(headers.map(escapeCSV).join(','))

  // Add data rows
  results.value.forEach(record => {
    const row = [
      record.id || '',
      record.account_id || '',
      record.user_id || '',
      record.account_type || '',
      record.username || '',
      record.patient_id || '',
      record.patient_name || '',
      record.action_type || '',
      record.resource_type || '',
      record.resource_id || '',
      record.api_method || '',
      record.ip_address || '',
      record.user_agent || '',
      record.session_id || '',
      record.success ? 'Yes' : 'No',
      record.error_message || '',
      escapeCSV(formatDetails(record.details)),
      formatDate(record.timestamp)
    ]
    csvRows.push(row.map(escapeCSV).join(','))
  })

  // Create CSV content
  const csvContent = csvRows.join('\n')

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  
  link.setAttribute('href', url)
  link.setAttribute('download', `audit_log_${new Date().toISOString().split('T')[0]}.csv`)
  link.style.visibility = 'hidden'
  
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
</script>

<style scoped>
.audit-log-page {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

h1 {
  margin-bottom: 20px;
}

.audit-form {
  margin-bottom: 30px;
  padding: 20px;
  border: 1px solid #ddd;
  background: #f9f9f9;
}

.form-row {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.form-field {
  flex: 1;
  min-width: 200px;
}

.form-field label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-field input,
.form-field select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.form-actions {
  margin-top: 20px;
}

.form-actions button {
  padding: 10px 20px;
  margin-right: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.form-actions button:hover {
  background: #f0f0f0;
}

.form-actions button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading {
  padding: 20px;
  text-align: center;
}

.results {
  margin-top: 30px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.results-summary {
  font-weight: 500;
}

.export-btn {
  padding: 8px 16px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.export-btn:hover {
  background: #0056b3;
}

.table-container {
  overflow-x: auto;
}

.audit-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.audit-table th,
.audit-table td {
  padding: 8px;
  border: 1px solid #ddd;
  text-align: left;
}

.audit-table th {
  background: #f0f0f0;
  font-weight: 600;
}

.audit-table tr:nth-child(even) {
  background: #f9f9f9;
}

.success {
  color: green;
  font-weight: 600;
}

.failure {
  color: red;
  font-weight: 600;
}

.details-cell {
  max-width: 300px;
  overflow: auto;
  white-space: pre-wrap;
  font-size: 11px;
}

.no-results {
  padding: 20px;
  text-align: center;
  color: #666;
}
</style>
